import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Divider, 
  Alert,
  Paper,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions
} from '@mui/material';
import { loginWithEmail, loginWithGoogle, resetPassword } from '../../Auth/FirebaseAuth';
import { useNavigate } from 'react-router-dom';
import GoogleIcon from '@mui/icons-material/Google';
import Footer from '../Footer/Footer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      await loginWithEmail(email, password);
      navigate('/');
    } catch (error: any) {
      setError('Failed to sign in: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/');
    } catch (error: any) {
      setError('Failed to sign in with Google: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openResetDialog = () => {
    setResetDialogOpen(true);
    setResetEmail(email); // Pre-fill with login email if available
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setError('Please enter an email address');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await resetPassword(resetEmail);
      setResetEmailSent(true);
      // Close the dialog after a small delay to show success
      setTimeout(() => {
        setResetDialogOpen(false);
      }, 1000);
    } catch (error: any) {
      setError('Failed to send password reset email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2 }}>
        <Box 
          sx={{
            display: 'flex',
            justifyContent: 'space-between', // Changed from 'center' to create space for button
            alignItems: 'center',
            mb: 2,
            mt: 0
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src="/UnitedInTalkLogo_Black.svg"
            alt="United In Talk"
            sx={{ 
              height: '50px',
              maxWidth: '60%'  // Reduced to make room for button
            }}
          />
          
          {/* Learn More Button - Modern Redesign */}
          <Button
            variant="text"
            size="small"
            color="primary"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 5,
              px: 2,
              py: 0.75,
              backgroundColor: 'rgba(25, 118, 210, 0.15)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.15)',
                transform: 'translateX(3px)'
              }
            }}
            onClick={() => window.open('https://explore.unitedintalk.com/about', '_blank')}
          >
            Learn More
          </Button>
        </Box>

        {/* Beta Label */}
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block', 
            textAlign: 'left', 
            mb: 0,
            mt: -1,
            fontSize: '.7rem',
            letterSpacing: '0.00em',
            opacity: 0.5,
            color: 'black',
            fontWeight: 200
          }}
        >
          This is a public beta. Account data may be deleted as we build. 
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Box component="form" onSubmit={handleEmailLogin}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            Sign In
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }}>OR</Divider>
        
        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleLogin}
          disabled={loading}
        >
          Sign in with Google
        </Button>
          <Box sx={{ mt: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button 
              onClick={() => navigate('/signup')}
              variant="text"
            >
              Don't have an account? Sign Up
            </Button>
            <Button 
              variant="text" 
              onClick={openResetDialog}
              size="small"
            >
              Forgot Password?
            </Button>
          </Box>
      </Paper>

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="resetEmail"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => {
            handlePasswordReset();
            setResetDialogOpen(false);
          }}>Send Reset Email</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;